import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import { mainBorrowerPricingReducer, initialState } from './reducer'
import { MainBorrowerPricingContext } from './MainBorrowerPricingContext'
import { createBorrowerPricingAsyncActions } from './actions/createAsyncActions'
import { createBorrowerPricingSyncActions } from './actions/createSyncActions'
import { createMainBorrowerPricingMiddleware } from './middlewares/createMainBorrowerPricingMiddleware'
import { MainBorrowerPricingAction } from './actions/types'
import { createPricingWatchdog } from './actions/pricingWatchdog'

export const MainBorrowerPricingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, baseDispatch] = useReducer(mainBorrowerPricingReducer, initialState)

  // Version stable de getState
  const stateRef = useRef(state)
  stateRef.current = state
  const getState = useCallback(() => stateRef.current, [])

  const middlewareRef = useRef<any>(null);

  // Dispatch avec middleware (utilise une ref pour éviter la dépendance circulaire)
  const dispatch = useCallback(
    (action: MainBorrowerPricingAction) => {
      if (middlewareRef.current) {
        middlewareRef.current(baseDispatch)(action);
      } else {
        baseDispatch(action);
      }
    },
    []
  );


  // syncActions ne dépend que de dispatch (stable)
  const syncActions = useMemo(
    () => createBorrowerPricingSyncActions(dispatch),
    [dispatch]
  )

  // Recovers the cards left pending when a SignalR pricing notification is lost
  const pricingWatchdog = useMemo(
    () => createPricingWatchdog(syncActions, getState),
    [syncActions, getState]
  )

  useEffect(() => () => pricingWatchdog.cancel(), [pricingWatchdog])

  // asyncActions avec getState stable
  const asyncActions = useMemo(
    () => createBorrowerPricingAsyncActions(syncActions, getState, pricingWatchdog),
    [syncActions, getState, pricingWatchdog]
  )

  const middleware = useMemo(
    () => createMainBorrowerPricingMiddleware(asyncActions),
    [asyncActions]
  );

  middlewareRef.current = middleware;

  // //TO REMOVE 
  // const prevBorrowerIdRef = useRef<string | null>(null);
  //
  // // Effet pour déclencher onAdjustment quand borrowerId change
  // useEffect(() => {
  //   const currentBorrowerId = state.borrowerId;
  //
  //   // Vérifier si borrowerId a changé et n'est pas null
  //   if (currentBorrowerId !== null && currentBorrowerId !== prevBorrowerIdRef.current) {
  //     // Ne pas appeler sur le premier montage si borrowerId est null initialement
  //     if (prevBorrowerIdRef.current !== null || currentBorrowerId !== initialState.borrowerId) {
  //       asyncActions.onAdjustments();
  //     }
  //     prevBorrowerIdRef.current = currentBorrowerId;
  //   }
  // }, [state.borrowerId, asyncActions]);

  const value = useMemo(() => ({
    state,
    dispatch,
    ...syncActions,
    ...asyncActions
  }), [state, dispatch, syncActions, asyncActions])

  return (
    <MainBorrowerPricingContext.Provider value={value}>
      {children}
    </MainBorrowerPricingContext.Provider>
  )
}
